
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import ScrollToTop from "./components/ScrollToTop";
import BreadcrumbSchema from "./components/seo/BreadcrumbSchema";
import PreloadImages from "./components/PreloadImages";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import WhySpharaka from "./pages/WhySpharaka";
import Products from "./pages/Products";
import ProductsLanding from "./pages/ProductsLanding";
import Solutions from "./pages/Solutions";
import ISHWAR from "./pages/products/ISHWAR";
import Enterprise from "./pages/solutions/Enterprise";
import SMB from "./pages/solutions/SMB";
import Government from "./pages/solutions/Government";
import About from "./pages/About";
import Careers from "./pages/Careers";
import MSSP from "./pages/partners/MSSP";
import Reseller from "./pages/partners/Reseller";
import Technology from "./pages/partners/Technology";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

import Blogs from "./pages/Blogs";
import CaseStudies from "./pages/CaseStudies";
import MSSPAgenticAI from "./pages/blog/MSSPAgenticAI";
import BuildingTrustAgenticAI from "./pages/blog/BuildingTrustAgenticAI";
import AIXDRSolutionGuide from "./pages/blog/AIXDRSolutionGuide";
import EDRNDRMDRXDRDifference from "./pages/blog/EDRNDRMDRXDRDifference";
import ReactiveToProactiveSOC from "./pages/blog/ReactiveToProactiveSOC";
import AIInXDRPlatforms from "./pages/blog/AIInXDRPlatforms";
import SIEMsFailingEnterprises from "./pages/blog/SIEMsFailingEnterprises";
import AutomatingIncidentResponse from "./pages/blog/AutomatingIncidentResponse";
import FutureOfXDR2024 from "./pages/blog/FutureOfXDR2024";
import UltimateGuideAIXDR from "./pages/blog/UltimateGuideAIXDR";
import AIXDRMultiCloud from "./pages/blog/AIXDRMultiCloud";
import RansomwareAsAService from "./pages/blog/RansomwareAsAService";
import IdentityBasedAttacks from "./pages/blog/IdentityBasedAttacks";
import AuraXPTechnology from "./pages/AURATechnology";
import AuthCallback from "./pages/AuthCallback";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
    },
  },
});

// Component to track page views
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if gtag is available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', 'G-L255DRYFZX', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <PreloadImages />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <BreadcrumbSchema />
        <PageTracker />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/why-spharaka" element={<WhySpharaka />} />
          <Route path="/products" element={<ProductsLanding />} />
          <Route path="/products/spharakaxdr" element={<Products />} />
          <Route path="/products/ishwar" element={<ISHWAR />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/solutions/enterprise" element={<Enterprise />} />
          <Route path="/solutions/smb" element={<SMB />} />
          <Route path="/solutions/government" element={<Government />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/partners/mssp" element={<MSSP />} />
          <Route path="/partners/reseller" element={<Reseller />} />
          <Route path="/partners/technology" element={<Technology />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          
          <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/ai-xdr-solution-guide" element={<AIXDRSolutionGuide />} />
            <Route path="/blogs/building-trust-agentic-ai" element={<BuildingTrustAgenticAI />} />
            <Route path="/blogs/mssp-agentic-ai" element={<MSSPAgenticAI />} />
            <Route path="/blogs/edr-ndr-mdr-xdr-difference" element={<EDRNDRMDRXDRDifference />} />
            <Route path="/blogs/reactive-to-proactive-soc" element={<ReactiveToProactiveSOC />} />
          <Route path="/blogs/ai-in-xdr-platforms" element={<AIInXDRPlatforms />} />
          <Route path="/blogs/siems-failing-enterprises" element={<SIEMsFailingEnterprises />} />
          <Route path="/blogs/automating-incident-response" element={<AutomatingIncidentResponse />} />
          <Route path="/blogs/future-of-xdr-2024" element={<FutureOfXDR2024 />} />
          <Route path="/blogs/ultimate-guide-ai-xdr" element={<UltimateGuideAIXDR />} />
          <Route path="/blogs/ai-xdr-multi-cloud" element={<AIXDRMultiCloud />} />
          <Route path="/blogs/ransomware-as-a-service" element={<RansomwareAsAService />} />
          <Route path="/blogs/identity-based-attacks" element={<IdentityBasedAttacks />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/products/auraxp" element={<AuraXPTechnology />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* Catch-all route for 404 errors - MUST be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
